import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Stack,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import api from '../services/api';

interface InvoiceOrder {
  orderId: string;
  orderNumber: string;
  source: 'storefront' | 'outlet' | 'mixed' | 'vip';
  createdAt: string;
  customer: string;
  customerPhone: string;
  customerEmail: string;
  shippingAddress: string;
  itemCount: number;
  totalAmount: number;        // net invoiceable amount (total − 斷貨 − 退貨, incl 運費)
  originalAmount: number;     // reconstructed original paid amount
  deduction: number;          // 斷貨 + 退貨 removed from the original
  status: string;
  deliveryType: 'CVS' | 'HOME' | 'VIP';
  receivedAt: string | null;  // pickup time (CVS) or ship date (宅配)
  daysSinceReceived: number | null;
  taxId?: TaxIdRecord | null;   // 統編 recorded for this order (customer request)
  invoice?: InvoiceData;
}

// A 統一編號 the customer asked for after checkout. Stored per order; issuing
// (single or batch) automatically turns that order into a 公司發票.
interface TaxIdRecord {
  customerIdentifier: string;
  customerName: string;
  customerAddr: string;
  note: string;
}

interface InvoiceData {
  id: number;
  order_id: string;
  platform: string;
  relate_number: string;
  invoice_no?: string;
  invoice_date?: string;
  random_number?: string;
  customer_identifier?: string;
  customer_name?: string;
  customer_addr?: string;
  customer_phone?: string;
  customer_email?: string;
  carrier_type?: string;
  carrier_num?: string;
  love_code?: string;
  print: string;
  donation: string;
  tax_type: string;
  sales_amount: number;
  status: 'pending' | 'issued' | 'voided';
  rtn_code?: string;
  rtn_msg?: string;
  created_at: string;
  updated_at: string;
}

interface InvoiceFormData {
  invoiceType: 'personal' | 'company' | 'donation';
  print: '0' | '1';
  donation: '0' | '1';
  carrierType: '' | '1' | '2' | '3';
  carrierNum: string;
  customerIdentifier: string;
  customerName: string;
  customerAddr: string;
  customerPhone: string;
  customerEmail: string;
  loveCode: string;
  taxType: '1' | '2' | '3';
  credentialType: string;
}

const ShopifyInvoicePage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<InvoiceOrder[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<'all' | 'issued' | 'not-issued' | 'voided'>('not-issued');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'storefront' | 'outlet' | 'vip'>('all');
  const [waitDays, setWaitDays] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  // Batch selection takes the FIRST N rows in table order, so the sort decides
  // which 300 get issued. Default 最舊優先: orders age out at 39 days after
  // receipt, so the oldest are the ones that must not be left behind.
  const [sortOrder, setSortOrder] = useState<'oldest' | 'newest'>('oldest');

  // Batch issuing: how many invoices to issue in one run, plus live progress.
  const [batchLimit, setBatchLimit] = useState(300);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number; ok: number; fail: number } | null>(null);
  const cancelBatchRef = useRef(false);

  // 統編 dialog state
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const [taxOrder, setTaxOrder] = useState<InvoiceOrder | null>(null);
  const [savingTaxId, setSavingTaxId] = useState(false);
  const [taxForm, setTaxForm] = useState({
    customerIdentifier: '',
    customerName: '',
    customerAddr: '',
    note: ''
  });

  // Invoice dialog state
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<InvoiceOrder | null>(null);
  const [invoiceFormData, setInvoiceFormData] = useState<InvoiceFormData>({
    invoiceType: 'personal',
    print: '0',
    donation: '0',
    carrierType: '1',
    carrierNum: '',
    customerIdentifier: '',
    customerName: '',
    customerAddr: '',
    customerPhone: '',
    customerEmail: '',
    loveCode: '168001',
    taxType: '1',
    credentialType: 'default'
  });
  const [issuingInvoice, setIssuingInvoice] = useState(false);

  // Void dialog state
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidingInvoice, setVoidingInvoice] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  // Fetch storefront orders eligible for invoicing. The backend gates on
  // "customer received ≥ waitDays ago" (CVS pickup time / 宅配 ship date),
  // excludes Hong Kong, and computes the net amount (total − 斷貨 − 退貨).
  // Already-invoiced orders are always included for history.
  const fetchFulfilledOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/ecpay/invoice/eligible-orders', {
        params: { days: waitDays }
      });
      const rows = res.data?.orders || [];

      const mapped: InvoiceOrder[] = rows.map((o: any) => ({
        orderId: o.orderId,
        orderNumber: o.orderId,
        source: ['outlet', 'mixed', 'vip'].includes(o.source) ? o.source : 'storefront',
        createdAt: o.createdAt ? moment(o.createdAt).format('YYYY-MM-DD HH:mm') : '',
        customer: o.customer || 'N/A',
        customerPhone: o.customerPhone || '',
        customerEmail: o.customerEmail || '',
        shippingAddress: o.shippingAddress || '',
        itemCount: o.itemCount || 0,
        totalAmount: Math.round(o.amount || 0),
        originalAmount: Math.round(o.originalAmount || 0),
        deduction: Math.round(o.deduction || 0),
        status: o.status || 'unknown',
        deliveryType: o.deliveryType === 'CVS' ? 'CVS' : o.deliveryType === 'VIP' ? 'VIP' : 'HOME',
        receivedAt: o.receivedAt || null,
        daysSinceReceived: o.daysSinceReceived ?? null,
        taxId: o.taxId || null,
        invoice: o.invoice || undefined
      }));

      setOrders(mapped);
      const vipLoaded = mapped.filter(o => o.source === 'vip').length;
      enqueueSnackbar(
        `已載入 ${mapped.length} 筆（官網 + Outlet 收貨滿 ${waitDays} 天` +
        `${vipLoaded > 0 ? `，含 VIP 年費 ${vipLoaded} 筆，不限時間` : ''}）`,
        { variant: 'success' }
      );
    } catch (error: any) {
      console.error('Error fetching eligible orders:', error);
      enqueueSnackbar('載入訂單失敗: ' + (error.response?.data?.error || error.message), {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, waitDays]);

  // Filter orders
  const filteredOrders = useMemo(() => {
    let filtered = orders;

    // Source filter ('mixed' orders contain outlet items → count as outlet)
    if (sourceFilter === 'storefront') {
      filtered = filtered.filter(order => order.source === 'storefront');
    } else if (sourceFilter === 'outlet') {
      filtered = filtered.filter(order => order.source === 'outlet' || order.source === 'mixed');
    } else if (sourceFilter === 'vip') {
      filtered = filtered.filter(order => order.source === 'vip');
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order =>
        order.orderNumber.toLowerCase().includes(term) ||
        order.customer.toLowerCase().includes(term) ||
        order.invoice?.invoice_no?.toLowerCase().includes(term)
      );
    }

    // Invoice status filter
    if (invoiceStatusFilter !== 'all') {
      filtered = filtered.filter(order => {
        if (invoiceStatusFilter === 'issued') {
          return order.invoice && order.invoice.status === 'issued';
        }
        if (invoiceStatusFilter === 'not-issued') {
          return !order.invoice || order.invoice.status === 'pending';
        }
        if (invoiceStatusFilter === 'voided') {
          return order.invoice && order.invoice.status === 'voided';
        }
        return true;
      });
    }

    // Sort by 收貨日 (falls back to order date) — this is also the order the
    // batch picker walks, so 最舊優先 clears the ones nearest the 39-day cutoff.
    const keyOf = (o: InvoiceOrder) => new Date(o.receivedAt || o.createdAt || 0).getTime();
    filtered = [...filtered].sort((a, b) =>
      sortOrder === 'oldest' ? keyOf(a) - keyOf(b) : keyOf(b) - keyOf(a)
    );

    return filtered;
  }, [orders, searchTerm, invoiceStatusFilter, sourceFilter, sortOrder]);

  // Selection handlers
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const selectableOrders = filteredOrders
        .filter(order => !order.invoice || order.invoice.status !== 'issued')
        .map(order => order.orderId);
      setSelectedOrders(selectableOrders);
    } else {
      setSelectedOrders([]);
    }
  }, [filteredOrders]);

  const handleSelectOrder = useCallback((orderId: string) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  }, []);

  // Open invoice dialog
  const handleOpenInvoiceDialog = useCallback((order: InvoiceOrder) => {
    setCurrentOrder(order);

    // Pre-fill form with order data. A recorded 統編 request pre-selects
    // 公司發票 so the operator doesn't have to remember it.
    const hasTaxId = !!order.taxId?.customerIdentifier;
    setInvoiceFormData({
      invoiceType: hasTaxId ? 'company' : 'personal',
      print: hasTaxId ? '1' : '0',
      donation: '0',
      carrierType: '1',
      carrierNum: '',
      customerIdentifier: order.taxId?.customerIdentifier || '',
      customerName: order.taxId?.customerName || order.customer,
      customerAddr: order.taxId?.customerAddr || order.shippingAddress,
      customerPhone: order.customerPhone,
      customerEmail: order.customerEmail || '',
      loveCode: '168001',
      taxType: '1',
      credentialType: 'default'
    });
    
    setInvoiceDialogOpen(true);
  }, []);

  // Issue single invoice
  const handleIssueInvoice = useCallback(async () => {
    if (!currentOrder) return;

    setIssuingInvoice(true);
    try {
      const requestData: any = {
        print: invoiceFormData.print,
        donation: invoiceFormData.donation,
        taxType: invoiceFormData.taxType,
        credentialType: invoiceFormData.credentialType
      };

      // Add fields based on invoice type
      if (invoiceFormData.invoiceType === 'personal') {
        requestData.carrierType = invoiceFormData.carrierType;
        if (invoiceFormData.carrierType === '3') {
          requestData.carrierNum = invoiceFormData.carrierNum;
        }
      } else if (invoiceFormData.invoiceType === 'company') {
        requestData.customerIdentifier = invoiceFormData.customerIdentifier;
        requestData.customerName = invoiceFormData.customerName;
        requestData.customerAddr = invoiceFormData.customerAddr;
        requestData.print = '1'; // Company invoices must be printed
      } else if (invoiceFormData.invoiceType === 'donation') {
        requestData.donation = '1';
        requestData.loveCode = invoiceFormData.loveCode;
        requestData.print = '0'; // Donations cannot be printed
      }

      // VIP 年費 rows live in membership_transactions, everything else (官網 /
      // Outlet / 混合) is looked up on the storefront side.
      requestData.platform = currentOrder.source === 'vip' ? 'vip' : 'storefront';

      const response = await api.post(
        `/api/ecpay/invoice/issue-for-order/${currentOrder.orderId}`,
        requestData
      );

      if (response.data.success) {
        enqueueSnackbar(
          `發票開立成功！發票號碼: ${response.data.invoiceNo}`,
          { variant: 'success', autoHideDuration: 6000 }
        );

        // Refresh orders to get updated invoice data
        await fetchFulfilledOrders();
        setInvoiceDialogOpen(false);
        setCurrentOrder(null);
      } else {
        throw new Error(response.data.message || '開立發票失敗');
      }
    } catch (error: any) {
      console.error('Error issuing invoice:', error);
      enqueueSnackbar(
        '開立發票失敗: ' + (error.response?.data?.error || error.message),
        { variant: 'error' }
      );
    } finally {
      setIssuingInvoice(false);
    }
  }, [currentOrder, invoiceFormData, enqueueSnackbar, fetchFulfilledOrders]);

  // Orders in the current view that can still be invoiced, in table order.
  const issuableOrders = useMemo(
    () => filteredOrders.filter(o => !o.invoice || o.invoice.status !== 'issued'),
    [filteredOrders]
  );

  // Select the first N issuable orders — the way to invoice "300 at a time"
  // without ticking 300 checkboxes.
  const handleSelectFirstN = useCallback(() => {
    const n = Math.max(1, batchLimit);
    const picked = issuableOrders.slice(0, n).map(o => o.orderId);
    setSelectedOrders(picked);
    enqueueSnackbar(
      picked.length < n
        ? `目前只有 ${picked.length} 筆可開立（少於 ${n} 筆）`
        : `已選取前 ${picked.length} 筆`,
      { variant: picked.length > 0 ? 'info' : 'warning' }
    );
  }, [batchLimit, issuableOrders, enqueueSnackbar]);

  // Batch issue invoices — capped at 批次張數, one order at a time (ECPay has no
  // bulk endpoint), with live progress and a stop button. Orders carrying a
  // recorded 統編 are issued as 公司發票 by the backend automatically.
  const handleBatchIssueInvoices = useCallback(async () => {
    // If nothing is ticked, fall back to the first N issuable rows so the
    // 張數 field alone is enough to run a batch.
    const queue = (selectedOrders.length > 0
      ? selectedOrders
      : issuableOrders.map(o => o.orderId)
    ).slice(0, Math.max(1, batchLimit));

    if (queue.length === 0) {
      enqueueSnackbar('沒有可開立發票的訂單', { variant: 'warning' });
      return;
    }

    const withTaxId = queue.filter(id => orders.find(o => o.orderId === id)?.taxId).length;

    const confirmed = window.confirm(
      `確定要開立 ${queue.length} 張發票嗎？\n` +
      `預設：個人發票（存入綠界載具）\n` +
      (withTaxId > 0 ? `其中 ${withTaxId} 筆已登記統編，將自動開立公司發票（列印）\n` : '') +
      `開立後無法直接修改，只能作廢重開。`
    );
    if (!confirmed) return;

    cancelBatchRef.current = false;
    setLoading(true);
    setBatchProgress({ done: 0, total: queue.length, ok: 0, fail: 0 });

    let successCount = 0;
    let failCount = 0;
    const failures: string[] = [];

    for (const orderId of queue) {
      if (cancelBatchRef.current) break;
      try {
        const requestBody: any = {
          print: '0',
          donation: '0',
          carrierType: '1',
          taxType: '1',
          credentialType: 'default',
          platform: orders.find(o => o.orderId === orderId)?.source === 'vip'
            ? 'vip'
            : 'storefront'
        };

        const response = await api.post(
          `/api/ecpay/invoice/issue-for-order/${orderId}`,
          requestBody
        );

        if (response.data.success) {
          successCount++;
        } else {
          failCount++;
          failures.push(`${orderId}: ${response.data.message || '未知錯誤'}`);
        }
      } catch (error: any) {
        console.error(`Error issuing invoice for order ${orderId}:`, error);
        failCount++;
        failures.push(`${orderId}: ${error.response?.data?.error || error.message}`);
      }
      // Snapshot the counters per iteration so the updater closes over
      // block-scoped values rather than the mutating loop variables.
      const ok = successCount;
      const fail = failCount;
      setBatchProgress(prev => prev ? { ...prev, done: prev.done + 1, ok, fail } : prev);
    }

    const stopped = cancelBatchRef.current;
    cancelBatchRef.current = false;
    setLoading(false);
    setBatchProgress(null);
    setSelectedOrders([]);

    if (failures.length > 0) {
      console.warn('批次開立失敗明細:\n' + failures.join('\n'));
    }

    if (successCount > 0) {
      enqueueSnackbar(
        `${stopped ? '已中止：' : ''}成功開立 ${successCount} 張發票` +
        (failCount > 0 ? `，失敗 ${failCount} 張（詳情見 Console）` : ''),
        { variant: failCount > 0 ? 'warning' : 'success', autoHideDuration: 8000 }
      );
    } else {
      enqueueSnackbar(
        stopped ? '批次已中止' : `批次開立發票失敗（${failCount} 張，詳情見 Console）`,
        { variant: 'error' }
      );
    }

    await fetchFulfilledOrders();
  }, [selectedOrders, issuableOrders, batchLimit, orders, enqueueSnackbar, fetchFulfilledOrders]);

  // --- 統編 (company tax ID) -------------------------------------------------
  const handleOpenTaxDialog = useCallback((order: InvoiceOrder) => {
    setTaxOrder(order);
    setTaxForm({
      customerIdentifier: order.taxId?.customerIdentifier || '',
      customerName: order.taxId?.customerName || '',
      customerAddr: order.taxId?.customerAddr || order.shippingAddress || '',
      note: order.taxId?.note || ''
    });
    setTaxDialogOpen(true);
  }, []);

  const handleSaveTaxId = useCallback(async () => {
    if (!taxOrder) return;

    const identifier = taxForm.customerIdentifier.trim();
    if (!/^\d{8}$/.test(identifier)) {
      enqueueSnackbar('統一編號必須是 8 位數字', { variant: 'warning' });
      return;
    }
    if (!taxForm.customerName.trim()) {
      enqueueSnackbar('請輸入公司抬頭', { variant: 'warning' });
      return;
    }
    // 統編 invoices are printed, and ECPay requires an address on printed
    // invoices. VIP payments have no shipping address to fall back on.
    if (taxOrder.source === 'vip' && !taxForm.customerAddr.trim()) {
      enqueueSnackbar('VIP 統編發票必須填寫公司地址', { variant: 'warning' });
      return;
    }

    setSavingTaxId(true);
    try {
      await api.post(`/api/ecpay/invoice/tax-id/${taxOrder.orderId}`, {
        customerIdentifier: identifier,
        customerName: taxForm.customerName.trim(),
        customerAddr: taxForm.customerAddr.trim(),
        note: taxForm.note.trim()
      });
      enqueueSnackbar(`已登記統編 ${identifier}，開立時會自動開公司發票`, { variant: 'success' });
      setTaxDialogOpen(false);
      setTaxOrder(null);
      await fetchFulfilledOrders();
    } catch (error: any) {
      enqueueSnackbar(
        '登記統編失敗: ' + (error.response?.data?.error || error.message),
        { variant: 'error' }
      );
    } finally {
      setSavingTaxId(false);
    }
  }, [taxOrder, taxForm, enqueueSnackbar, fetchFulfilledOrders]);

  const handleDeleteTaxId = useCallback(async () => {
    if (!taxOrder) return;
    setSavingTaxId(true);
    try {
      await api.delete(`/api/ecpay/invoice/tax-id/${taxOrder.orderId}`);
      enqueueSnackbar('已移除統編登記', { variant: 'success' });
      setTaxDialogOpen(false);
      setTaxOrder(null);
      await fetchFulfilledOrders();
    } catch (error: any) {
      enqueueSnackbar(
        '移除統編失敗: ' + (error.response?.data?.error || error.message),
        { variant: 'error' }
      );
    } finally {
      setSavingTaxId(false);
    }
  }, [taxOrder, enqueueSnackbar, fetchFulfilledOrders]);

  // Open void dialog
  const handleOpenVoidDialog = useCallback((order: InvoiceOrder) => {
    if (!order.invoice || order.invoice.status !== 'issued') {
      enqueueSnackbar('此訂單尚未開立發票或已作廢', { variant: 'warning' });
      return;
    }
    setCurrentOrder(order);
    setVoidReason('');
    setVoidDialogOpen(true);
  }, [enqueueSnackbar]);

  // Void invoice
  const handleVoidInvoice = useCallback(async () => {
    if (!currentOrder || !currentOrder.invoice) return;

    if (!voidReason.trim()) {
      enqueueSnackbar('請輸入作廢原因', { variant: 'warning' });
      return;
    }

    setVoidingInvoice(true);
    try {
      const response = await api.post('/api/ecpay/invoice/void', {
        invoiceNo: currentOrder.invoice.invoice_no,
        invoiceDate: currentOrder.invoice.invoice_date,
        reason: voidReason
      });

      if (response.data.success) {
        enqueueSnackbar('發票作廢成功', { variant: 'success' });
        await fetchFulfilledOrders();
        setVoidDialogOpen(false);
        setCurrentOrder(null);
        setVoidReason('');
      } else {
        throw new Error(response.data.message || '作廢發票失敗');
      }
    } catch (error: any) {
      console.error('Error voiding invoice:', error);
      enqueueSnackbar(
        '作廢發票失敗: ' + (error.response?.data?.error || error.message),
        { variant: 'error' }
      );
    } finally {
      setVoidingInvoice(false);
    }
  }, [currentOrder, voidReason, enqueueSnackbar, fetchFulfilledOrders]);

  // Initial load
  useEffect(() => {
    fetchFulfilledOrders();
  }, [fetchFulfilledOrders]);

  // Get invoice status chip
  const getInvoiceStatusChip = (invoice?: InvoiceData) => {
    if (!invoice) {
      return <Chip label="未開立" color="default" size="small" />;
    }

    switch (invoice.status) {
      case 'issued':
        return <Chip icon={<CheckCircleIcon />} label="已開立" color="success" size="small" />;
      case 'voided':
        return <Chip icon={<CancelIcon />} label="已作廢" color="error" size="small" />;
      case 'pending':
        return <Chip icon={<WarningIcon />} label="處理中" color="warning" size="small" />;
      default:
        return <Chip label="未知" color="default" size="small" />;
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          發票管理系統
        </Typography>

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={fetchFulfilledOrders}
          disabled={loading}
        >
          重新載入
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                總訂單數
              </Typography>
              <Typography variant="h4">{orders.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                已開立發票
              </Typography>
              <Typography variant="h4" color="success.main">
                {orders.filter(o => o.invoice?.status === 'issued').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                未開立發票
              </Typography>
              <Typography variant="h4" color="warning.main">
                {orders.filter(o => !o.invoice || o.invoice.status !== 'issued').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                已作廢
              </Typography>
              <Typography variant="h4" color="error.main">
                {orders.filter(o => o.invoice?.status === 'voided').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="搜尋訂單"
              placeholder="訂單編號、客戶名稱、發票號碼"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <CloseIcon />
                  </IconButton>
                )
              }}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>訂單來源</InputLabel>
              <Select
                value={sourceFilter}
                label="訂單來源"
                onChange={(e) => setSourceFilter(e.target.value as any)}
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="storefront">官網</MenuItem>
                <MenuItem value="outlet">Outlet</MenuItem>
                <MenuItem value="vip">VIP 年費</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>發票狀態</InputLabel>
              <Select
                value={invoiceStatusFilter}
                label="發票狀態"
                onChange={(e) => setInvoiceStatusFilter(e.target.value as any)}
              >
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="issued">已開立</MenuItem>
                <MenuItem value="not-issued">未開立</MenuItem>
                <MenuItem value="voided">已作廢</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="收貨滿幾天"
              value={waitDays}
              onChange={(e) => setWaitDays(Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0)))}
              inputProps={{ min: 0, max: 60 }}
              helperText="鑑賞期過後才開立"
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>排序</InputLabel>
              <Select
                value={sortOrder}
                label="排序"
                onChange={(e) => setSortOrder(e.target.value as any)}
              >
                <MenuItem value="oldest">收貨日 舊→新</MenuItem>
                <MenuItem value="newest">收貨日 新→舊</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              可開立 {issuableOrders.length} 筆
            </Typography>
          </Grid>

          {/* Batch controls: pick how many invoices to issue in one run */}
          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <TextField
                size="small"
                type="number"
                label="批次張數"
                value={batchLimit}
                onChange={(e) => setBatchLimit(Math.max(1, Math.min(1000, parseInt(e.target.value, 10) || 1)))}
                inputProps={{ min: 1, max: 1000 }}
                helperText="一次最多開立幾張"
                sx={{ width: 160 }}
                disabled={loading}
              />

              <Stack direction="row" spacing={1} flexWrap="wrap">
                {[50, 100, 300, 500].map(n => (
                  <Chip
                    key={n}
                    label={n}
                    size="small"
                    variant={batchLimit === n ? 'filled' : 'outlined'}
                    color={batchLimit === n ? 'primary' : 'default'}
                    onClick={() => setBatchLimit(n)}
                    disabled={loading}
                  />
                ))}
              </Stack>

              <Button
                variant="outlined"
                onClick={handleSelectFirstN}
                disabled={loading || issuableOrders.length === 0}
              >
                選取前 {batchLimit} 筆
              </Button>

              <Button
                variant="contained"
                startIcon={<ReceiptIcon />}
                onClick={handleBatchIssueInvoices}
                disabled={loading || issuableOrders.length === 0}
              >
                批次開立 {Math.min(
                  batchLimit,
                  selectedOrders.length > 0 ? selectedOrders.length : issuableOrders.length
                )} 張
              </Button>

              {selectedOrders.length > 0 && (
                <Button size="small" onClick={() => setSelectedOrders([])} disabled={loading}>
                  清除選取 ({selectedOrders.length})
                </Button>
              )}

              <Typography variant="caption" color="text.secondary">
                依目前排序（{sortOrder === 'oldest' ? '舊→新' : '新→舊'}）由上往下開立
              </Typography>
            </Stack>

            {batchProgress && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    開立中 {batchProgress.done} / {batchProgress.total}
                    　成功 {batchProgress.ok}
                    {batchProgress.fail > 0 && `　失敗 ${batchProgress.fail}`}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => { cancelBatchRef.current = true; }}
                  >
                    停止
                  </Button>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(batchProgress.done / batchProgress.total) * 100}
                />
                <Typography variant="caption" color="text.secondary">
                  開立期間請勿關閉此頁面
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Orders Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedOrders.length > 0 &&
                    selectedOrders.length < filteredOrders.filter(o => !o.invoice || o.invoice.status !== 'issued').length
                  }
                  checked={
                    filteredOrders.filter(o => !o.invoice || o.invoice.status !== 'issued').length > 0 &&
                    selectedOrders.length === filteredOrders.filter(o => !o.invoice || o.invoice.status !== 'issued').length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>訂單編號</TableCell>
              <TableCell>收貨日</TableCell>
              <TableCell>訂單日期</TableCell>
              <TableCell>客戶</TableCell>
              <TableCell>統編</TableCell>
              <TableCell>商品數量</TableCell>
              <TableCell align="right">訂單金額</TableCell>
              <TableCell>發票狀態</TableCell>
              <TableCell>發票號碼</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Alert severity="info">沒有符合條件的訂單</Alert>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => (
                <TableRow
                  key={order.orderId}
                  hover
                  sx={{
                    backgroundColor: order.invoice?.status === 'issued'
                      ? 'rgba(76, 175, 80, 0.05)'
                      : order.invoice?.status === 'voided'
                      ? 'rgba(244, 67, 54, 0.05)'
                      : 'inherit'
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedOrders.includes(order.orderId)}
                      onChange={() => handleSelectOrder(order.orderId)}
                      disabled={order.invoice?.status === 'issued'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      #{order.orderNumber}
                    </Typography>
                    {order.source !== 'storefront' && (
                      <Chip
                        label={
                          order.source === 'mixed' ? '混合'
                            : order.source === 'vip' ? 'VIP 年費'
                            : 'Outlet'
                        }
                        size="small"
                        color={order.source === 'vip' ? 'warning' : 'secondary'}
                        variant="outlined"
                        sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {order.receivedAt ? (
                      <Box>
                        <Typography variant="body2">
                          {moment(order.receivedAt).format('YYYY-MM-DD')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.deliveryType === 'VIP' ? '付款'
                            : order.deliveryType === 'CVS' ? '取貨' : '出貨'}
                          {order.daysSinceReceived != null ? ` · ${order.daysSinceReceived} 天前` : ''}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.createdAt}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{order.customer}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.customerPhone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {order.taxId ? (
                      <Tooltip title={`${order.taxId.customerName || ''}${order.taxId.note ? ` · ${order.taxId.note}` : ''}｜點擊修改`}>
                        <Chip
                          icon={<BusinessIcon />}
                          label={order.taxId.customerIdentifier}
                          size="small"
                          color="info"
                          onClick={() => handleOpenTaxDialog(order)}
                          disabled={order.invoice?.status === 'issued'}
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </Tooltip>
                    ) : order.invoice?.status === 'issued' ? (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    ) : (
                      <Tooltip title="客戶要求統編發票時登記">
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<BusinessIcon fontSize="small" />}
                          onClick={() => handleOpenTaxDialog(order)}
                          sx={{ minWidth: 0, px: 0.5, fontSize: '0.75rem' }}
                        >
                          加統編
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.source === 'vip' ? '年費 1 式' : `${order.itemCount} 件`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      NT$ {order.totalAmount.toLocaleString()}
                    </Typography>
                    {order.deduction > 0 && (
                      <Typography variant="caption" color="error.main" display="block">
                        原 NT$ {order.originalAmount.toLocaleString()}（斷貨/退貨 -{order.deduction.toLocaleString()}）
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {getInvoiceStatusChip(order.invoice)}
                  </TableCell>
                  <TableCell>
                    {order.invoice?.invoice_no ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {order.invoice.invoice_no}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.invoice.invoice_date}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {!order.invoice || order.invoice.status !== 'issued' ? (
                        <Tooltip title="開立發票">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenInvoiceDialog(order)}
                          >
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="作廢發票">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenVoidDialog(order)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invoice Issue Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          開立發票 - 訂單 #{currentOrder?.orderNumber}
        </DialogTitle>
        <DialogContent dividers>
          {currentOrder && (
            <Box>
              {/* Order Summary */}
              <Alert severity={currentOrder.deduction > 0 ? 'warning' : 'info'} sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>客戶:</strong> {currentOrder.customer}
                  <br />
                  <strong>開立金額:</strong> NT$ {currentOrder.totalAmount.toLocaleString()}
                  {currentOrder.deduction > 0 && (
                    <>
                      {' '}
                      <Typography component="span" variant="caption" color="error.main">
                        （原 NT$ {currentOrder.originalAmount.toLocaleString()} − 斷貨/退貨 NT$ {currentOrder.deduction.toLocaleString()}）
                      </Typography>
                    </>
                  )}
                  <br />
                  <strong>{currentOrder.source === 'vip' ? '項目' : '商品'}:</strong>{' '}
                  {currentOrder.source === 'vip'
                    ? 'Metanoia VIP 年費會員 × 1'
                    : `${currentOrder.itemCount} 件`}
                </Typography>
              </Alert>

              {/* Invoice Type Selection */}
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend">發票類型</FormLabel>
                <RadioGroup
                  row
                  value={invoiceFormData.invoiceType}
                  onChange={(e) => setInvoiceFormData({
                    ...invoiceFormData,
                    invoiceType: e.target.value as any,
                    donation: e.target.value === 'donation' ? '1' : '0',
                    print: e.target.value === 'company' ? '1' : '0'
                  })}
                >
                  <FormControlLabel
                    value="personal"
                    control={<Radio />}
                    label="個人發票（載具）"
                  />
                  <FormControlLabel
                    value="company"
                    control={<Radio />}
                    label="公司發票（統一編號）"
                  />
                  <FormControlLabel
                    value="donation"
                    control={<Radio />}
                    label="捐贈發票"
                  />
                </RadioGroup>
              </FormControl>

              {/* Personal Invoice Fields */}
              {invoiceFormData.invoiceType === 'personal' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>載具類別</InputLabel>
                      <Select
                        value={invoiceFormData.carrierType}
                        label="載具類別"
                        onChange={(e) => setInvoiceFormData({
                          ...invoiceFormData,
                          carrierType: e.target.value as any
                        })}
                      >
                        <MenuItem value="1">綠界電子發票載具</MenuItem>
                        <MenuItem value="3">手機條碼</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {invoiceFormData.carrierType === '3' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="手機條碼"
                        placeholder="/ABC1234"
                        value={invoiceFormData.carrierNum}
                        onChange={(e) => setInvoiceFormData({
                          ...invoiceFormData,
                          carrierNum: e.target.value
                        })}
                        helperText="請輸入以 / 開頭的手機條碼"
                      />
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Company Invoice Fields */}
              {invoiceFormData.invoiceType === 'company' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="統一編號"
                      placeholder="12345678"
                      value={invoiceFormData.customerIdentifier}
                      onChange={(e) => setInvoiceFormData({
                        ...invoiceFormData,
                        customerIdentifier: e.target.value
                      })}
                      inputProps={{ maxLength: 8 }}
                      helperText="8位數字"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="公司名稱"
                      value={invoiceFormData.customerName}
                      onChange={(e) => setInvoiceFormData({
                        ...invoiceFormData,
                        customerName: e.target.value
                      })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="公司地址"
                      value={invoiceFormData.customerAddr}
                      onChange={(e) => setInvoiceFormData({
                        ...invoiceFormData,
                        customerAddr: e.target.value
                      })}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Donation Invoice Fields */}
              {invoiceFormData.invoiceType === 'donation' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="捐贈碼"
                      value={invoiceFormData.loveCode}
                      onChange={(e) => setInvoiceFormData({
                        ...invoiceFormData,
                        loveCode: e.target.value
                      })}
                      helperText="請輸入愛心碼（預設: 168001 創世基金會）"
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleIssueInvoice}
            disabled={issuingInvoice}
            startIcon={issuingInvoice ? <CircularProgress size={20} /> : <ReceiptIcon />}
          >
            {issuingInvoice ? '開立中...' : '確認開立'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 統編 Dialog — record a customer's company-invoice request */}
      <Dialog
        open={taxDialogOpen}
        onClose={() => setTaxDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          登記統一編號 - 訂單 #{taxOrder?.orderNumber}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            登記後，這筆訂單（單張或批次）都會自動開立<strong>公司發票</strong>（列印、不存載具）。
            發票開立後就不能再加統編，只能作廢重開。
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                autoFocus
                label="統一編號"
                placeholder="12345678"
                value={taxForm.customerIdentifier}
                onChange={(e) => setTaxForm({
                  ...taxForm,
                  customerIdentifier: e.target.value.replace(/\D/g, '').slice(0, 8)
                })}
                inputProps={{ maxLength: 8, inputMode: 'numeric' }}
                error={!!taxForm.customerIdentifier && !/^\d{8}$/.test(taxForm.customerIdentifier)}
                helperText="8 位數字"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="公司抬頭"
                value={taxForm.customerName}
                onChange={(e) => setTaxForm({ ...taxForm, customerName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required={taxOrder?.source === 'vip'}
                label="公司地址"
                value={taxForm.customerAddr}
                onChange={(e) => setTaxForm({ ...taxForm, customerAddr: e.target.value })}
                helperText={taxOrder?.source === 'vip'
                  ? 'VIP 年費沒有收件地址，必填'
                  : '留空則使用訂單收件地址'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="備註"
                placeholder="例如：客戶 LINE 於 8/7 要求"
                value={taxForm.note}
                onChange={(e) => setTaxForm({ ...taxForm, note: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {taxOrder?.taxId && (
            <Button
              color="error"
              onClick={handleDeleteTaxId}
              disabled={savingTaxId}
            >
              移除統編
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setTaxDialogOpen(false)}>取消</Button>
          <Button
            variant="contained"
            onClick={handleSaveTaxId}
            disabled={savingTaxId}
            startIcon={savingTaxId ? <CircularProgress size={20} /> : <BusinessIcon />}
          >
            {savingTaxId ? '儲存中...' : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void Invoice Dialog */}
      <Dialog
        open={voidDialogOpen}
        onClose={() => setVoidDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          作廢發票
        </DialogTitle>
        <DialogContent dividers>
          {currentOrder?.invoice && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                您確定要作廢以下發票嗎？此操作無法復原！
              </Alert>

              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>訂單編號:</strong> #{currentOrder.orderNumber}
                <br />
                <strong>發票號碼:</strong> {currentOrder.invoice.invoice_no}
                <br />
                <strong>發票日期:</strong> {currentOrder.invoice.invoice_date}
                <br />
                <strong>金額:</strong> NT$ {currentOrder.totalAmount.toLocaleString()}
              </Typography>

              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="作廢原因"
                placeholder="請輸入作廢原因（例如：訂單取消、客戶要求等）"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                helperText="作廢原因將記錄在系統中"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidDialogOpen(false)}>
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleVoidInvoice}
            disabled={voidingInvoice || !voidReason.trim()}
            startIcon={voidingInvoice ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {voidingInvoice ? '作廢中...' : '確認作廢'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShopifyInvoicePage;